/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PermissionedComponent from '@/components/PermissionedComponent';
import { useCache } from '@/hooks/use-cache';
import { useFetchFiles } from '@/hooks/use-fetch-files';
import { cn } from '@/lib/utils';
import { CloudUpload, FileCheck, FileX, X } from 'lucide-react';
import { v4 as uuid } from 'uuid';

import { ShouldRender } from '../ShouldRender';
import { Button } from './button';
import { FormItem } from './form-item';
import Loading from './loading';
import { Select } from './select';
import { useToast } from './use-toast';

export type Upload = {
  classNames?: {
    container?: string;
    root?: string;
  };
  onChange?: ({ target: { name, value } }: { target: { name: string; value: File[] } }) => void;
  onValueChange?: (value: File[]) => void;
  value?: File[];
  name?: string;
  onError?: (error: string) => void;
  showUploadList?: boolean;
  onRemoveFile?: (file: DocumentData) => void;
  allowedExtensions?: string[];
  maxFiles?: number;
  disabled?: boolean;
  onUpload?: (file: File[]) => void;
  description?: string;
  permissions?: string | string[];
  fieldsConfig?: {
    type?: boolean;
  };
  isPresign?: boolean;
  isLoading?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
  additionalFields?: (file: File, index: number) => React.ReactNode;
  shortDetails?: boolean;
  onTypeChange?: (file: File, index: number, typeId: string) => void;
};

export type UploadRef = {
  removeFile: (index: number) => void;
  removeFileByName: (name: string) => void;
};

export const Upload = forwardRef<UploadRef, Upload>(
  (
    {
      name,
      onChange,
      classNames,
      value = [],
      onError,
      onRemoveFile,
      showUploadList,
      allowedExtensions = [
        '.csv',
        '.bmp',
        '.jpeg',
        '.jpg',
        '.png',
        '.gif',
        '.json',
        '.xls',
        '.xlsx',
        '.doc',
        '.docx',
        '.ppt',
        '.pptx',
        '.pdf',
        '.txt',
        '.xml',
        '.xlsm',
        '.msg',
        '.gtm',
        '.zip',
        '.kml',
        '.eml',
        '.oft',
      ],
      maxFiles = 0,
      disabled,
      onValueChange,
      onUpload,
      description,
      permissions,
      fieldsConfig,
      isPresign,
      isLoading,
      onLoadingChange,
      additionalFields,
      shortDetails: _shortDetails = false,
      onTypeChange,
    }: Upload,
    ref: React.Ref<UploadRef>,
  ) => {
    const { toast } = useToast();
    const { t } = useTranslation();
    const cache = useCache();
    const { handleGetUrlUpload } = useFetchFiles();

    const inputRef = useRef<HTMLInputElement>(null);

    const [_, setDragging] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [loadingFile, setLoadingFile] = useState(false);
    const [messageError, setMessageError] = useState('');
    const [id] = useState(uuid());

    useEffect(() => {
      onLoadingChange?.(loadingFile);
    }, [loadingFile]);

    const isDisabled = (value?.length >= maxFiles && !!maxFiles) || disabled;

    const handleDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragIn = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragOut = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        await processFiles(Array.from(e.dataTransfer.files));
        e.dataTransfer.clearData();
      }
    };

    const handlePresignUpload = async (file: any) => {
      try {
        const response = await handleGetUrlUpload(file.name, file, setLoadingFile);
        if (!response) {
          setHasError(true);
          setMessageError(t('components.upload.errorUpload.title', { fileName: file.name }));
          removeFile(value.indexOf(file));
          return null;
        }
        return response.filename;
      } catch (error) {
        console.error(error);
        return null;
      }
    };

    const processFiles = async (files: File[]) => {
      setHasError(false);
      let validFiles = files.filter(file => handleValidateFile(file));

      if (validFiles.length) {
        if (isPresign) {
          const presignPromises = validFiles.map(file => handlePresignUpload(file));
          const presignedFileNames = await Promise.all(presignPromises);

          let validFilesClone = validFiles
            .filter((f, index) => !!f && !!presignedFileNames[index])
            .map((file, index) => new File([file], presignedFileNames[index] || file.name));

          if (onUpload) {
            const response = await onUpload(validFilesClone);
            if (Array.isArray(response)) {
              validFilesClone = response;
            }
          }
          if (onValueChange) onValueChange([...(value || []), ...validFilesClone]);

          if (onChange && name) onChange({ target: { name, value: [...(value || []), ...validFilesClone] } });
        } else {
          if (onUpload) {
            const response = await onUpload(validFiles);
            if (Array.isArray(response)) {
              validFiles = response;
            }
          }
          if (onValueChange) onValueChange([...(value || []), ...validFiles]);

          if (onChange && name) onChange({ target: { name, value: [...(value || []), ...validFiles] } });
        }
      }
    };

    const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files || [];
      await processFiles(Array.from(files));
    };

    const handleValidateFile = (file: File) => {
      if (file.size / 1024 > 50000) {
        setHasError(true);
        setMessageError(t('components.upload.fileTooBig.title'));
        if (onError) onError(t('components.upload.fileTooBig.title'));
        toast({
          title: t('components.upload.fileTooBig.title'),
          description: t('components.upload.fileTooBig.description'),
        });
        return false;
      }
      return true;
    };

    const removeFile = (index: number) => {
      if (onChange && name) {
        const newValue = value.filter((_, i) => i !== index);
        if (onValueChange) onValueChange(newValue);
        onChange({ target: { name, value: newValue } });
        onRemoveFile?.(value[index]);
        if (inputRef.current) inputRef.current.value = '';
      }
    };

    const removeFileByName = (name: string) => {
      if (onChange && name) {
        const newValue = value.filter(file => file.name !== name);
        if (onValueChange) onValueChange(newValue);
        onChange({ target: { name, value: newValue } });
        if (inputRef.current) inputRef.current.value = '';
      }
    };

    useImperativeHandle(ref, () => ({
      removeFile,
      removeFileByName,
    }));

    return (
      <div
        className={cn(
          'flex min-h-fit w-full flex-wrap items-center overflow-hidden rounded-lg border border-dashed bg-white',
          hasError ? 'border-red-600' : '',
        )}
      >
        <PermissionedComponent permission={permissions}>
          <Loading isLoading={loadingFile || !!isLoading} className="w-full">
            <label
              htmlFor={id}
              className={cn(
                'custom-file-input w-full overflow-hidden [&:focus-within>*]:bg-blue-100',
                classNames?.container,
              )}
            >
              <div
                className="w-full"
                onDragOver={handleDrag}
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDrop={handleDrop}
              >
                <input
                  id={id}
                  type="file"
                  ref={inputRef}
                  accept={allowedExtensions?.join(',')}
                  className="h-0 w-0 opacity-0"
                  disabled={isDisabled}
                  onChange={handleChangeFile}
                  multiple={maxFiles !== 1}
                />
                <div
                  className={cn(
                    'text-mg shadow-sm relative flex w-full flex-col items-center gap-2 border-b border-dashed border-gray-100 p-4 font-bold text-gray-400',
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                    hasError ? 'text-red-600' : '',
                  )}
                >
                  {hasError ? <FileX size={30} /> : <CloudUpload size={30} />}
                  <span className="text-center">{hasError ? messageError : t('components.upload.description')}</span>
                  {description ? <span className="text-center text-gray-500">{description}</span> : null}
                  {!hasError && allowedExtensions?.length ? (
                    <span
                      className="font-sm text-center font-normal text-gray-400"
                      title={allowedExtensions.join(', ')}
                    >
                      {`${t('components.upload.allowedExtensions', { extensions: allowedExtensions.slice(0, 5).join(', ') })} ${
                        allowedExtensions.length > 5 ? '...' : ''
                      }`}
                    </span>
                  ) : null}
                </div>
              </div>
            </label>
          </Loading>
        </PermissionedComponent>

        <div className={cn('w-full transition-all', showUploadList && value?.length ? 'h-auto' : 'h-0')}>
          <table className="relative w-full @container">
            <tbody className="text-mg max-h-[500px] overflow-y-auto font-bold text-gray-500">
              {value &&
                value.map((file, index) => (
                  <tr key={file.name} className="border-b border-gray-100">
                    <td className="w-[30%] min-w-[150px] px-4 py-3 align-top @sm:w-[40%]">
                      <div className="flex items-center gap-4">
                        <FileCheck size={30} className="mt-1 flex-shrink-0" />
                        <div className="flex min-w-0 flex-col gap-2">
                          <div className="font-bold">{t('components.upload.fileName')}</div>
                          <div
                            title={file?.name?.replace(/[A-z0-9][^_]*_/g, '') ?? ''}
                            className="line-clamp-2 max-w-[350px] overflow-hidden text-ellipsis break-words text-sm font-normal"
                          >
                            {isPresign ? file?.name?.replace(/[A-z0-9][^_]*_/g, '') : file?.name ?? ''}
                          </div>
                          <ShouldRender condition={!isPresign}>
                            <div className="flex flex-col">
                              <div className="font-bold">{t('components.upload.fileSize')}</div>
                              <div className="text-sm font-normal">{(file.size / 1024).toFixed(2)} KB</div>
                            </div>
                          </ShouldRender>
                        </div>
                      </div>
                    </td>
                    <td className="relative flex w-full items-start overflow-hidden px-4 py-3 align-top">
                      {fieldsConfig?.type ? (
                        <FormItem
                          name={`${name}[${index}].type_id`}
                          rules={{ required: true }}
                          label={<div className="font-bold text-sm">{t('file-identification')}</div>}
                          readable={t('file-identification')}
                          className="relative w-full overflow-hidden"
                        >
                          <Select
                            className="relative w-full"
                            options={cache.getCreditOptions('document_type_credit')}
                            onChange={event => {
                              const typeId = event.target.value as string;
                              if (onTypeChange && typeId) {
                                onTypeChange(file, index, typeId);
                              }
                            }}
                          />
                        </FormItem>
                      ) : null}
                      {additionalFields ? additionalFields(file, index) : null}
                    </td>
                    <td className="col-span-1 w-10 items-center justify-end px-4 py-3">
                      <Button
                        variant="destructive"
                        size="sm"
                        type="button"
                        className="p-1"
                        onClick={() => removeFile(value.indexOf(file))}
                      >
                        <X size={18} />
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);
